import {
  collectEmptyValueTokens,
  type PreviewValue,
  renderMailPreviewSegments,
} from "./scheduleMailPreview";
import {
  SCHEDULE_MAIL_PARAMETERS,
  type ScheduleMailParameterDefinition,
} from "./scheduleMailTemplate";

type PreviewValues = Record<string, PreviewValue>;

/**
 * 送信時にどんなメールが届くかを、パラメータを値に置き換えて見せる。
 *
 * 値のマップを受け取るだけにしてあるので、日程調整メール以外のパラメータセットでも使える。
 */
export function MailPreview({
  subject,
  body,
  values,
  parameters,
  unsavableTokens = [],
}: {
  subject: string;
  body: string;
  values: PreviewValues;
  parameters: ScheduleMailParameterDefinition[];
  /** 値が空だと保存できないトークン名。空欄のまま送信される注意からは外す */
  unsavableTokens?: string[];
}) {
  const subjectSegments = renderMailPreviewSegments(subject, values);
  const bodySegments = renderMailPreviewSegments(body, values);
  const valueSegments = [...subjectSegments, ...bodySegments].filter(
    (segment) => segment.type === "value" && segment.text !== "",
  );
  const hasValue = valueSegments.length > 0;
  const hasSample = valueSegments.some(
    (segment) => segment.type === "value" && !segment.actual,
  );
  const emptyTokens = [
    ...new Set([
      ...collectEmptyValueTokens(subjectSegments),
      ...collectEmptyValueTokens(bodySegments),
    ]),
  ];
  // 置換は全10トークンで行うので、ラベルの解決も全件から引く。ユースケースで絞ると、
  // そのユースケースの一覧に無いトークンを本文に手で書いたときだけ注意が出ない
  const toLabels = (names: string[]) =>
    names
      .map(
        (name) =>
          parameters.find((p) => p.name === name)?.label ??
          SCHEDULE_MAIL_PARAMETERS.find((p) => p.name === name)?.label,
      )
      .filter((label): label is string => Boolean(label));
  const unsavableLabels = toLabels(
    emptyTokens.filter((name) => unsavableTokens.includes(name)),
  );
  const emptyLabels = toLabels(
    emptyTokens.filter((name) => !unsavableTokens.includes(name)),
  );

  return (
    <details className="mt-2 rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)] p-2">
      <summary className="cursor-pointer text-[var(--color-text-secondary)] text-xs">
        プレビュー（実際に届くメール）
      </summary>

      {unsavableLabels.length > 0 && (
        <p className="mt-2 text-[var(--color-text-danger)] text-xs">
          {unsavableLabels.join("・")}
          が未設定のため、保存できません。{unsavableLabels.join("・")}
          を入れるか、文面から{unsavableLabels.join("・")}
          を消すと保存できます。
        </p>
      )}

      {emptyLabels.length > 0 && (
        <p className="mt-2 text-[var(--color-text-danger)] text-xs">
          {emptyLabels.join("・")}
          が未設定のため、その箇所は空欄のまま送信されます
        </p>
      )}

      {hasValue && (
        <p className="mt-2 text-[var(--color-text-secondary)] text-xs">
          <span className="mail-preview-value">水色</span>
          はパラメータが置き換わったところです。
          {hasSample && (
            <>
              このうち「（サンプル）」と書いた箇所は、送信時に対象者ごとの値になります。
            </>
          )}
        </p>
      )}

      <div className="mt-2 max-h-[320px] overflow-auto rounded border border-[var(--color-border-default)] bg-white p-3">
        {/* 編集欄と同じ「件名」「本文」で見出しを付ける。太字だけだと、どこまでが件名か読めない */}
        <p className="mb-1 font-medium text-[11px] text-[var(--color-text-secondary)]">
          件名
        </p>
        {/* 予約URLのような折り返せない長い文字列がカードからはみ出すので break-words を付ける */}
        <p
          data-testid="mail-preview-subject"
          className="break-words font-bold text-[var(--color-text-primary)] text-sm"
        >
          <PreviewText segments={subjectSegments} />
        </p>
        <hr className="my-2 border-[var(--color-border-default)]" />
        <p className="mb-1 font-medium text-[11px] text-[var(--color-text-secondary)]">
          本文
        </p>
        <p
          data-testid="mail-preview-body"
          className="whitespace-pre-wrap break-words text-[var(--color-text-primary)] text-sm"
        >
          <PreviewText segments={bodySegments} />
        </p>
      </div>
    </details>
  );
}

function PreviewText({
  segments,
}: {
  segments: ReturnType<typeof renderMailPreviewSegments>;
}) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.type === "value" && segment.text !== "" ? (
          <span
            // 同じ値が複数回出るので、位置でしか区別できない
            key={`${segment.name}-${index}`}
            className="mail-preview-value"
          >
            {segment.text}
            {/* 色だけだと仮の値だと気づかないので、サンプルには言葉でも添える。
                プレビューの表示だけの飾りで、実際に送る文面には入らない */}
            {segment.actual ? null : "（サンプル）"}
          </span>
        ) : (
          <span key={`text-${index}`}>{segment.text}</span>
        ),
      )}
    </>
  );
}
